import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageCircle, ShoppingBag, Package, ReceiptText,
  CreditCard, Tag, Users, Settings as SettingsIcon, Menu, LogOut,
  ChevronLeft, PanelLeft, Sparkles, UploadCloud, Repeat, Wallet, Scale, Radio, Store, FileText, KeyRound, Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/components/BrandProvider";
import { BACKEND_URL } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "operador", "contadora"], primary: true },
  { to: "/conversaciones", label: "Conversaciones", icon: MessageCircle, roles: ["admin", "operador"], primary: true },
  { to: "/pedidos", label: "Pedidos", icon: ShoppingBag, roles: ["admin", "operador"], primary: true },
  { to: "/productos", label: "Productos", icon: Package, roles: ["admin", "operador", "contadora"], primary: true },
  { to: "/importar-catalogo", label: "Importar catálogo", icon: UploadCloud, roles: ["admin", "contadora"] },
  { to: "/facturas", label: "Cargar factura", icon: ReceiptText, roles: ["admin", "contadora"] },
  { to: "/pagos", label: "Pagos", icon: CreditCard, roles: ["admin", "operador", "contadora"] },
  { to: "/seguimientos", label: "Seguimientos", icon: Repeat, roles: ["admin", "operador"] },
  { to: "/rendiciones", label: "Rendiciones", icon: Wallet, roles: ["admin", "contadora"] },
  { to: "/conciliacion", label: "Conciliación", icon: Scale, roles: ["admin", "contadora"] },
  { to: "/facturacion", label: "Facturación", icon: FileText, roles: ["admin", "contadora"] },
  { to: "/promociones", label: "Promociones", icon: Tag, roles: ["admin", "operador"] },
  { to: "/clientes", label: "Clientes", icon: Users, roles: ["admin", "operador"] },
  { to: "/canales", label: "Canales", icon: Radio, roles: ["admin"] },
  { to: "/shopify", label: "Shopify", icon: Store, roles: ["admin", "contadora"] },
  { to: "/estado-sistema", label: "Estado del sistema", icon: Activity, roles: ["admin"] },
  { to: "/configuracion", label: "Configuración", icon: SettingsIcon, roles: ["admin"] },
];

// Fix K (2026-09-22): el encabezado del menú dibujaba un ícono y un nombre escritos a mano.
// Ahora usa el ícono y el nombre de Configuración → Marca; el destello queda solo como respaldo.
function Wordmark({ collapsed }) {
  const marca = useBrand() || {};
  const nombre = (marca.nombre_comercial || "").trim() || "Marthitashop";
  return (
    <div className="flex items-center gap-2 select-none min-w-0">
      {marca.logo_icono_url ? (
        <img src={`${BACKEND_URL}${marca.logo_icono_url}`} alt={nombre}
          className="h-9 w-9 rounded-2xl object-contain shrink-0" data-testid="sidebar-logo-icono" />
      ) : (
        <div className="h-9 w-9 rounded-2xl bg-primary flex items-center justify-center shrink-0 shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.6)]">
          <Sparkles className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} />
        </div>
      )}
      {!collapsed && (
        // Fix O (2026-09-24): text-lg para que "Marthita Shop" entre completo junto a la campana y el botón
        // de colapsar; si un nombre largo no entra, se ve entero al pasar el mouse.
        <span className="font-head text-lg font-semibold tracking-tight truncate" title={nombre} data-testid="sidebar-nombre-comercial">
          {nombre}
        </span>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = () => { logout(); nav("/login"); };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-primary text-primary-foreground shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.5)]" : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-border bg-card/60 backdrop-blur-sm transition-all duration-300 ${
          collapsed ? "w-[76px]" : "w-64"
        }`}
      >
        <div className="flex items-center justify-between p-4">
          <Wordmark collapsed={collapsed} />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              data-testid="sidebar-toggle"
              onClick={() => setCollapsed((c) => !c)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {collapsed ? <PanelLeft className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkClass} data-testid={`nav-${n.label.toLowerCase()}`}>
              <n.icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          {!collapsed && (
            <div className="px-2 pb-2">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          )}
          <Button variant="ghost" onClick={() => setChangePwOpen(true)} data-testid="change-password-button" className="w-full justify-start gap-3 rounded-2xl text-foreground/70">
            <KeyRound className="h-5 w-5" strokeWidth={1.5} /> {!collapsed && "Cambiar contraseña"}
          </Button>
          <Button variant="ghost" onClick={doLogout} data-testid="logout-button" className="w-full justify-start gap-3 rounded-2xl text-foreground/70">
            <LogOut className="h-5 w-5" strokeWidth={1.5} /> {!collapsed && "Cerrar sesión"}
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-30 glass-nav flex items-center justify-between px-4 h-14 border-b border-border">
          <Wordmark collapsed={false} />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="p-2 -mr-2 text-foreground/70"><Menu className="h-6 w-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="p-4 border-b border-border">
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
                <nav className="p-3 space-y-1">
                  {items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setSheetOpen(false)} className={linkClass}>
                      <n.icon className="h-5 w-5" strokeWidth={1.5} /> <span>{n.label}</span>
                    </NavLink>
                  ))}
                  <button onClick={() => { setSheetOpen(false); setChangePwOpen(true); }} data-testid="change-password-button-mobile" className="w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm text-foreground/70 hover:bg-accent">
                    <KeyRound className="h-5 w-5" strokeWidth={1.5} /> Cambiar contraseña
                  </button>
                  <button onClick={doLogout} className="w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm text-red-500 hover:bg-red-50">
                    <LogOut className="h-5 w-5" strokeWidth={1.5} /> Cerrar sesión
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-24 md:pb-0">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 glass-nav h-16 flex items-stretch justify-around px-2">
          {items.filter((n) => n.primary).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 flex-1 text-[11px] ${isActive ? "text-primary" : "text-muted-foreground"}`}
              data-testid={`bottomnav-${n.label.toLowerCase()}`}>
              <n.icon className="h-5 w-5" strokeWidth={1.5} /><span>{n.label}</span>
            </NavLink>
          ))}
          <button onClick={() => setSheetOpen(true)} className="flex flex-col items-center justify-center gap-1 flex-1 text-[11px] text-muted-foreground" data-testid="bottomnav-mas">
            <Menu className="h-5 w-5" strokeWidth={1.5} /><span>Más</span>
          </button>
        </nav>
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
